import {
  computed,
  nextTick,
  onMounted,
  ref,
} from 'vue';
import { pick } from 'lodash';
import md5 from 'md5';
import { useAcl, useApiFieldErrors, useI18n, useMixpanel, usePlanLimits, useReportForm, useReportUtils, useTutorialIntro, useTutorialApi, useWorkspace, useCurrencyList } from '@/composables';
import { $emitter } from '@/services/emitter';
import availableTokens from '@/views/reports/forms/offer/config/availableTokens';
import {
  getOfferApiRequest,
  getOffersListApiRequest,
  postCreateOfferApiRequest,
  putUpdateOfferApiRequest,
} from '@/services/api/offers-api';
import {
  populateOffersStoreCacheApiRequest,
  updateOffersStoreCacheApiRequest,
} from '@/services/api/api-services/offers';
import { getAffiliateNetworkApiRequest } from '@/services/api/affiliate-networks-api';
import { populateAffiliateNetworksStoreCacheApiRequest } from '@/services/api/api-services/affiliate-networks';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { useStore } from '@/store';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';

export type OfferDialogMode = 'create' | 'edit' | 'duplicate';

export type OfferDialogData = {
  mode?: OfferDialogMode;
  id?: string;
  workspaceId?: string;
  onClose?: (result: unknown) => void;
  onEdit?: (data: unknown) => void;
};

type FailoverOffer = {
  id: string;
  name: string;
};

type OfferForm = {
  name: string;
  currencyId: string;
  countryId: number;
  payoutType: string;
  affiliateNetworkId: string;
  dailyCapFailoverOffer: FailoverOffer;
  payoutSum: number;
  enableDailyCap: boolean | number;
  dailyCapLimit: number;
  dailyCapTimezone: string;
  status: string;
  url: string;
  tags?: Array<{ id?: string; name: string } | string>;
  workspaceId?: string;
  clickIdParam?: string;
  created?: string;
  updated?: string;
  updatedBy?: string;
};

type AffiliateTemplateData = {
  clickIdPlaceholder: string;
  payoutPlaceholder: string;
  transactionIdPlaceholder: string;
  statusPlaceholder: string;
  integrationType?: string;
};

const EDIT_OFFER_FIELDS = [
  'name',
  'countryId',
  'tags',
  'url',
  'affiliateNetworkId',
  'clickIdParam',
  'currencyId',
  'payoutType',
  'payoutSum',
  'enableDailyCap',
  'dailyCapLimit',
  'dailyCapTimezone',
  'status',
  'workspaceId',
  'created',
  'updated',
  'updatedBy',
] as const;

const DUPLICATE_OFFER_FIELDS = [
  'name',
  'countryId',
  'tags',
  'url',
  'affiliateNetworkId',
  'clickIdParam',
  'currencyId',
  'payoutType',
  'payoutSum',
  'enableDailyCap',
  'dailyCapLimit',
  'dailyCapTimezone',
  'status',
  'workspaceId',
] as const;

const defaultTemplateData = (): AffiliateTemplateData => ({
  clickIdPlaceholder: 'REPLACE',
  payoutPlaceholder: 'OPTIONAL',
  transactionIdPlaceholder: 'OPTIONAL',
  statusPlaceholder: 'OPTIONAL',
});

export const useOfferDialog = (
  mode: OfferDialogMode,
  getData: () => OfferDialogData,
  emitClose: () => void,
) => {
  const store = useStore();
  const workspaceApi = useWorkspace();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const { trackMixpanel } = useMixpanel();
  const tagsPlanLimits = usePlanLimits('tagsOffers');
  const { currencyListOrderedPreferred, currencies } = useCurrencyList();
  const edit = ref(mode === 'edit');
  const show = ref(false);
  const submitLoading = ref(false);
  const offerId = ref('');
  const disableWorkspaceSelect = ref(false);
  const emptyUrlSend = ref(false);
  const oldHash = ref('');
  const lastAffiliateNetworkId = ref<string | null>('');
  const clickIdParam = ref<string | false>(false);
  const clickIdLong = ref(false);
  const offersLoaded = ref(false);
  const offerOptions = ref<Offer[]>([]);
  const templateData = ref<AffiliateTemplateData>(defaultTemplateData());

  const defaultChips = availableTokens();

  const defaultForm = (): OfferForm => ({
    name: '',
    currencyId: store.user.currencyId,
    countryId: 0,
    payoutType: 'auto',
    affiliateNetworkId: '',
    dailyCapFailoverOffer: {
      id: '',
      name: '',
    },
    payoutSum: 0,
    enableDailyCap: 0,
    dailyCapLimit: 0,
    dailyCapTimezone: store.user.defaultTimezone,
    status: 'active',
    url: '',
  });

  const form = ref<OfferForm>(defaultForm());

  const reportForm = useReportForm(() => ({
    edit: edit.value,
    mode: edit.value ? 'edit' : 'create',
  }));

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Offer Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of offer')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Offer Settings')}</div><div class='introjs-tooltip__content'>${t('Enter the URL of offer element')}</div>`,
    4: `<div class='introjs-tooltip__title'>${t('Offer Settings')}</div><div class='introjs-tooltip__content'>${t('Create offer element')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'offers',
  }));

  const tutorial = useTutorialApi();

  const {
    fieldErrors,
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const reportUtilsApi = useReportUtils({});

  const modalTitle = computed(() => {
    if (mode === 'edit') return t('Edit Offer');
    if (mode === 'duplicate') return t('Duplicate Offer');
    return t('Create Offer');
  });

  const countryListPlusGlobal = computed(() => store.countryListPlusGlobal);

  const countryName = computed(() => {
    if (!form.value.countryId) return '';
    return countryListPlusGlobal.value?.find(
      (item: { id: number }) => item.id === form.value.countryId,
    )?.name || '';
  });

  const nameDescription = computed(() => {
    const countryLabel = countryName.value || t('Global');
    const nameLabel = form.value.name || t('None');
    return `${countryLabel} - ${nameLabel}`;
  });

  const workspacesList = computed(() => reportForm.workspacesList.value);
  const defaultWorkspace = computed(() => reportForm.defaultWorkspace.value);
  const timezoneList = store.timezoneList;

  const affiliatesList = computed(() => {
    const allWorkspaces = [
      ...new Set([form.value.workspaceId, ...workspaceApi.allPublicWorkspacesIds.value]),
    ];
    const noneOption = [{ name: t('None'), id: '' }];
    const filteredAffiliateNetworks = store.cache.affiliateNetworks.data.filter(
      (affiliateNetwork: { workspaceId: string }) =>
        allWorkspaces.includes(affiliateNetwork.workspaceId),
    );
    return noneOption.concat(filteredAffiliateNetworks);
  });

  const offerOptionsWithoutEdited = computed(() => {
    if (mode === 'edit') {
      return offerOptions.value.filter((item) => item.id !== offerId.value);
    }
    return offerOptions.value;
  });

  const isActionsDisabled = computed(() => {
    if (mode !== 'edit') return false;
    return !!aclReadOnly({
      workspaceId: form.value.workspaceId,
      id: offerId.value,
      type: 'offers',
    });
  });

  const showPlanLimitsWarning_tags = computed(
    () => !!form.value.tags?.length && tagsPlanLimits.hasPlanLimitsRestriction('tagsOffers'),
  );

  const chips = computed(() => {
    let chipList = defaultChips;
    if (clickIdLong.value) {
      chipList = chipList.concat(['+ tid={clickIdLong}']);
    }
    return chipList.map((tag) => ({
      tag,
      disabled: false,
    }));
  });

  const postbackUrlParams = computed(() => [
    `cid=${templateData.value.clickIdPlaceholder || 'REPLACE'}`,
    `payout=${templateData.value.payoutPlaceholder || 'OPTIONAL'}`,
    `txid=${templateData.value.transactionIdPlaceholder || 'OPTIONAL'}`,
    `status=${templateData.value.statusPlaceholder || 'OPTIONAL'}`,
  ]);

  const postbackUrlProtocol = computed(() => {
    const row = store.trackingDomainsObject[store.user.trackingDomainId];
    return row?.httpsAvailable ? 'https' : 'http';
  });

  const postbackUrl = computed(() => {
    const row = store.trackingDomainsObject[store.user.trackingDomainId];
    return `${postbackUrlProtocol.value}://${row?.domain}/postback?${postbackUrlParams.value.join('&')}`;
  });

  const fetchOfferOptions = computed(() => {
    const { countryId } = form.value;
    if (store.cache.offers.isAllInCache) {
      return undefined;
    }
    return ({ search }: { search: string }) =>
      getOffersListApiRequest({
        search,
        status: 'active',
        limit: '200',
        orderBy: 'updated',
        ...(countryId && { countryId }),
        orderDirection: 'desc',
        columns: [
          'id',
          'name',
          'countryId',
          'affiliateNetworkId',
          'affiliateNetworkName',
          'countryName',
          'url',
        ],
        workspaceId: form.value.workspaceId,
      })
        .then((data) => {
          if (mode === 'edit') {
            return data.filter((item) => (item as Offer).id !== offerId.value);
          }
          return data;
        });
  });

  const enableDailyCap = computed({
    get: () => !!form.value.enableDailyCap,
    set: (value: boolean) => {
      if (!offersLoaded.value && value) {
        nextTick(() => {
          form.value.enableDailyCap = false;
          store.setLoading(true);
          loadDailyCapOffers()
            .then(() => {
              form.value.enableDailyCap = true;
              offersLoaded.value = true;
            })
            .finally(() => {
              store.setLoading(false);
            });
        });
      } else {
        form.value.enableDailyCap = value;
      }
    },
  });

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const processForm = (source: OfferForm) => {
    const preparedForm = {
      ...source,
      dailyCapFailoverOfferId: source.dailyCapFailoverOffer?.id || '',
    } as Record<string, unknown>;
    delete preparedForm.dailyCapFailoverOffer;
    preparedForm.tags = (source.tags || []).map((item) =>
      typeof item === 'object' && item?.id ? item.name : item,
    );
    return preparedForm;
  };

  const patchEmptyUrlFieldError = () => {
    if (!emptyUrlSend.value || !fieldErrors.value.url) return;

    fieldErrors.value = {
      ...fieldErrors.value,
      url: [t('CUSTOM_VALIDATION_EMPTY_URL')],
    };

    const items = [{ message: t('Validation failed') }];
    Object.entries(fieldErrors.value).forEach(([field, messages]) => {
      messages.forEach((message) => {
        const hasFieldPrefix = message.includes('→');
        const prefixedMessage = hasFieldPrefix ? message : `${t(field)} → ${message}`;
        items.push({ message: prefixedMessage, type: field });
      });
    });
    errors.value = items;
    errorsControlActive.value = items.length > 0;
  };

  const applySubmitErrors = (error: unknown) => {
    const axiosError = error as { message?: string; response?: { status?: number } };
    if (
      axiosError.response?.status === 403
      || axiosError.response?.status === 400
      || axiosError.message === 'Network Error'
    ) {
      return;
    }
    if (axiosError.response?.status === 409 && showPlanLimitsErrorInToast(error)) {
      return;
    }
    if (!axiosError.response) {
      return;
    }

    applyApiErrors(error);
    patchEmptyUrlFieldError();
  };

  const setOfferOptions = () => {
    const {
      uploadedForWorkspace,
      isAllInCache,
      data,
      isUploaded,
    } = store.cache.offers;

    if (isUploaded && isAllInCache) {
      offerOptions.value = data.filter(
        (item: Offer) =>
          workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
          || item.workspaceId === form.value.workspaceId,
      );
      return Promise.resolve();
    }

    if (isUploaded && !isAllInCache && uploadedForWorkspace === form.value.workspaceId) {
      offerOptions.value = [...data];
      return Promise.resolve();
    }

    if (form.value.workspaceId === defaultWorkspace.value.id) {
      return updateOffersStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      }).then((result) => {
        offerOptions.value = result as Offer[];
      });
    }

    return getOffersListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: [
        'id',
        'name',
        'countryId',
        'workspaceId',
        'affiliateNetworkId',
        'affiliateNetworkName',
        'countryName',
        'url',
      ],
      workspaceId: form.value.workspaceId,
    }).then((result) => {
      offerOptions.value = result as Offer[];
    });
  };

  const loadDailyCapOffers = async () => {
    if (!store.cache.offers.isUploaded) {
      await populateOffersStoreCacheApiRequest();
    }
    await setOfferOptions();
  };

  const getChangeAffiliatesPromise = () =>
    new Promise<void>((resolve, reject) => {
      if (
        lastAffiliateNetworkId.value
        && form.value.affiliateNetworkId === lastAffiliateNetworkId.value
      ) {
        resolve();
        return;
      }

      lastAffiliateNetworkId.value = form.value.affiliateNetworkId;

      const setDefault = () => {
        templateData.value = defaultTemplateData();
        clickIdParam.value = false;
      };

      if (form.value.affiliateNetworkId) {
        getAffiliateNetworkApiRequest(form.value.affiliateNetworkId)
          .then((data) => {
            if (data.templateId && data.affiliateNetworkTemplate) {
              templateData.value = data.affiliateNetworkTemplate as AffiliateTemplateData;
            } else {
              templateData.value = defaultTemplateData();
            }

            clickIdParam.value = data.appendClickId ? (data.clickIdParam as string) : false;
            clickIdLong.value = templateData.value.integrationType === 'clickbank';
            resolve();
          })
          .catch(() => {
            setDefault();
            reject();
          });
      } else {
        setDefault();
        resolve();
      }
    });

  const changeAffiliates = async () => {
    await getChangeAffiliatesPromise();
  };

  const loadOfferFromApi = async (id: string) => {
    const offer = await getOfferApiRequest(id).then((data) => {
      data.payoutSum = data.payoutSum || 0;
      const fields = mode === 'edit' ? EDIT_OFFER_FIELDS : DUPLICATE_OFFER_FIELDS;
      form.value = pick(data, fields) as unknown as OfferForm;
      form.value.dailyCapFailoverOffer = {
        id: data.dailyCapFailoverOfferId as string,
        name: data.dailyCapFailoverOfferName as string,
      };
      return data;
    });

    if (offer.enableDailyCap) {
      await loadDailyCapOffers();
    }
  };

  const loadCreateForm = (workspaceId?: string) =>
    reportForm.loadForm({
      defaultForm,
      form: form.value,
      workspaceId,
    });

  const loadData = async () => {
    const data = getData();

    if (mode === 'edit') {
      edit.value = true;
      offerId.value = data.id || (store.getTableSelectedItem().id as string);
      lastAffiliateNetworkId.value = null;
      store.setLoading(true);
      try {
        await loadOfferFromApi(offerId.value);
        await getChangeAffiliatesPromise();
        show.value = true;
        oldHash.value = getCurrentHash();
      } finally {
        store.setLoading(false);
      }
      return;
    }

    if (mode === 'duplicate') {
      store.setLoading(true);
      lastAffiliateNetworkId.value = form.value.affiliateNetworkId;
      try {
        const sourceId = store.getTableSelectedItem().id as string;
        await loadOfferFromApi(sourceId);
        form.value.name = `${form.value.name} (${t('____x.copy')})`;
        offerId.value = '';
        await getChangeAffiliatesPromise();
        show.value = true;
        oldHash.value = getCurrentHash();
      } finally {
        store.setLoading(false);
      }
      return;
    }

    store.setLoading(true);
    offersLoaded.value = false;
    try {
      await loadCreateForm(data.workspaceId);
      if (data.workspaceId) {
        form.value.workspaceId = data.workspaceId;
        disableWorkspaceSelect.value = true;
      }
      tutorial.form();
      await getChangeAffiliatesPromise();
      show.value = true;
      oldHash.value = getCurrentHash();
    } finally {
      store.setLoading(false);
    }
  };

  const onChangeWorkspace = () => {
    const { affiliateNetworkId, dailyCapFailoverOffer } = defaultForm();
    clickIdParam.value = false;
    form.value = {
      ...form.value,
      affiliateNetworkId,
      dailyCapFailoverOffer,
    };
    setOfferOptions();
  };

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const clearTags = () => {
    form.value.tags = [];
  };

  const trackSubmit = () => {
    const { enableDailyCap, name, dailyCapLimit } = form.value;
    const eventName = mode === 'edit' ? 'offer_edit' : 'offer_create';
    trackMixpanel(eventName, {
      name,
      ...(enableDailyCap && { daily_cap_limit: dailyCapLimit }),
    });
  };

  const submit = async () => {
    store.setLoading(true);
    submitLoading.value = true;
    emptyUrlSend.value = !form.value.url;
    clearErrors();

    try {
      if (mode === 'edit') {
        const result = await putUpdateOfferApiRequest(offerId.value, processForm(form.value));
        await refreshUserProfileFromApi();
        oldHash.value = getCurrentHash();
        reportUtilsApi.updateName('offers', result.id as string);
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Offer has been changed'));
        getData()?.onEdit?.(result);
        trackSubmit();
        close();
        return;
      }

      const result = await postCreateOfferApiRequest(processForm(form.value));
      await refreshUserProfileFromApi();
      oldHash.value = getCurrentHash();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Offer has been saved'));
      trackSubmit();
      close();
      getData()?.onClose?.(result);
    } catch (error) {
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
      submitLoading.value = false;
    }
  };

  onMounted(async () => {
    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }
    if (!store.cache.tags.offers.isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'offers' });
    }
    if (!store.cache.affiliateNetworks.isUploaded) {
      await populateAffiliateNetworksStoreCacheApiRequest();
    }
    await loadData();
  });

  return {
    store,

    t,
    show,
    submitLoading,
    form,
    offerId,
    disableWorkspaceSelect,
    errors,
    errorsControlActive,
    errorsDict,
    modalTitle,
    chips,
    countryListPlusGlobal,
    nameDescription,
    workspacesList,
    isActionsDisabled,
    showPlanLimitsWarning_tags,
    affiliatesList,
    clickIdParam,
    currencyListOrderedPreferred,
    currencies,
    enableDailyCap,
    timezoneList,
    offerOptionsWithoutEdited,
    fetchOfferOptions,
    postbackUrl,
    tutorial,
    tutorialIntro,
    onChangeWorkspace,
    changeAffiliates,
    checkClose,
    clearTags,
    submit,
    getIntroParams: tutorialIntro.getIntroParams,
  };
};
